/**
 * Created by Jiu.
 * Description: Added "All Notes" and "My Notes" page
 *
 * ------ maintenance history ------
 * 04/15/2019   Daniel Wang Load all research screen
 * 05/15/2019   Daniel Wang Load all dashboard screen
 * 05/28/2019   Yoyo Fang   <research-screen> changed to be conigurated by single object
 * 06/04/2019   Daniel Wang Share layout
 * 06/26/2019   Bowen Li:   Hide the setting menu in ipad
 * 06/26/2019   Yoyo Fang   use priorityTaskQueue to optimize prepareStore workflow for page loading ux
 * 07/12/2019   Daniel Wang Add screen/entity dictionary store to avoid sending duplicate request.
 * 08/02/2019   Yoyo Fang   research screen initialization logic move to ngrx store effect
 * 08/21/2019   Ella Ma     delete tab-set input data and add deleteTab callback
 * 08/21/2019   Yoyo Fang   adding new tab workflow, wait until store state ready for research
 * 08/29/2019   Yoyo Fang   adding new tab workflow, wait until store state ready for dashboard
 * 09/04/2019   Yoyo Fang   on destroyed, cleanup ngrx store state.
 * 06/30/2022   Daniel Wang Load workflow on homescreen
 */
import { Component, OnInit, ViewChild, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subscription, Observable, Subject, of } from 'rxjs';
import { map, skip, take, filter, mergeMap, catchError, switchMap } from 'rxjs/operators';
import { AppState, smartSelector } from '../../redux';
import { UUID } from 'angular2-uuid';

import {
    ResearchScreenSetRelationship, ResearchScreenActionTypes, ResearchScreenInitialization,
    ResearchScreenInitializeFocus, ResearchScreenInitializeLayout
} from '../../redux/actions/research-screen.actions';
import { StoreQuerierService } from '../../services/store-querier.service';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { ResearchFocusStatus, FinderSetting } from '../../components/finder/finder.view-model';
import { ResearchViewInfo, ResearchScreenInitializationPayload } from '../research/research.view-model';
import { EntityBrief } from '../../tamalelibs/models/entity-brief.model';
import { Entity } from '../../tamalelibs/models/entity.model';
import { RelationshipTreeService } from '../../services/relationship-tree.service';
import { ResearchHelperService } from '../../services/research-helper.service';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { ContextMenuService, ContextMenuComponent } from '@perfectmemory/ngx-contextmenu';
import { ScreenMenu } from './home-screen.model';
import { visibleScreensSelector } from '../../redux/reducers/system.reducer';
import {
    getHomeScreenSelector, getShareHomeScreenLayoutSelector, getChangeHomeScreenLayoutSelector,
} from '../../redux/reducers/home-screen.reducer';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { HomeScreenService } from '../../services/home-screen.service';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AUTO_SAVE_INTERVAL_5 } from '../../tamalelibs/constants/business.constants';
import { researchChangeStatusSelector } from '../../redux/reducers/research-screen.reducer';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { ResearchViewConstants, DashboardViewConstants } from '../../tamalelibs/models/smart-reducer.model';
import { DashboardScreenInitializeFocus } from '../../redux/actions/dashboard-screen.actions';
import { FocusInfo } from '../../components/tam-dashboard/tam-dashboard.model';
import { ResearchScreenConfigs } from '../../components/research-screen/research-screen.model';
import { businessConstants } from '../../constants/business.constants';
import { HomeScreenActionTypes } from '../../redux/actions/home-screen.actions';
import { PriorityTaskQueue, PriorityTaskQueueTask, AllExecuted, TaskExecuted } from '../../tamalelibs/services/priorityTaskQueue.service';
import { LayoutService } from '../../services/layout.service';
import { ScreenHelperService } from '../../services/screen-helper.service';
import { SystemActionTypes } from '../../tamalelibs/redux/actions/system.actions';
import { userSettingSelector } from '../../redux/reducers/users.reducer';
import { UsersActionTypes } from '../../redux/actions/users.actions';
import { PrintService } from '../../services/print.service';
import { FloatMenuService } from '../../services/float-menu.service';

@Component({
    selector: 'tam-home-screen',
    templateUrl: './home-screen.component.html',
    styleUrls: ['./home-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeScreenComponent implements OnInit, OnDestroy {
    @ViewChild('screensMenu', { static: false }) menuEle: ContextMenuComponent<any>;

    //#region Public properties
    actionSubject$: Subject<any> = new Subject(); // loading dashboard when adding new dashboard tab or changing dashboard tab
    currentPageId: string;
    currentPageName: string;
    currentUser: SystemUser;
    contextMenuConfig: ContextMenuConfig;
    draggableTab = true;
    firstTabPageId: string; // for record the first dashboard tab id
    homeScreen: any;
    isIPAD = false;
    isPrepareDataReady = false;
    isScreenLoading = {};
    loadingSpecificScreen = false; // for dashboard/workflow screens loading in homescreen
    pages: Array<any> = [];
    reloadingDashboard = false;
    reloadingWorkflow = false;
    selectedIndex = 0;
    screenMenuContainer: ScreenMenu[];
    //#endregion

    //#region Private properties
    private _delaySelectIndex = -1;
    private _destroySubscriptions: Array<Subscription> = [];
    private _errorPages: Array<any> = [];
    private _prepareStoreSubscription: Subscription = null;
    private _queue: PriorityTaskQueue = null;
    private _onlyUpdateStore = false;
    private _timer: any;
    //#endregion

    //#region constructor
    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _contextMenuService: ContextMenuService<any>,
        private _deviceService: DeviceDetectorService,
        private _floatMenuService: FloatMenuService,
        private _homeScreenService: HomeScreenService,
        private _layoutService: LayoutService,
        private _printService: PrintService,
        private _researchHelper: ResearchHelperService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _screenHelperService: ScreenHelperService,
        private _toast: ToastService,
    ) {
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }
    //#endregion

    //#region Public properties
    ngOnInit() {
        // init layout
        this._init();
        if (!this._changeDetectorRef['destroyed']) {
            // set the top of the sub-chrome on homescreen, all other top of sub-chrome is 119px
            this._layoutService.changeSubTitleTopOpen$.next('111px');
        }

        // subscribe home screen to load data
        this._store.pipe(
            select(userSettingSelector),
            filter(data => data),
            take(1),
        ).subscribe(() => {
            this._destroySubscriptions.push(
                this._store.pipe(
                    select(getHomeScreenSelector),
                    filter(data => data)
                ).subscribe((res) => {
                    this._onSubscribeHomeScreens(res);
                })
            );
        });

        this._destroySubscriptions.push(
            // subscribe layout share
            this._store.pipe(
                select(getShareHomeScreenLayoutSelector)).pipe(skip(1)
                ).subscribe(res => this._onSubscribeShareHomeScreenLayout(res)),

            // subscribe layout change
            this._store.pipe(
                select(getChangeHomeScreenLayoutSelector)
            ).subscribe(res => this._onSubscribeChangeHomeScreenLayout(res)),

            // subscribe the research change status for auto save
            this._store.pipe(
                select(researchChangeStatusSelector)
            ).subscribe(researchStatus => {
                if (researchStatus) {
                    this._onSubscribeResearchChangeStatus(researchStatus);
                }
            }),

            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),

            // subscribe the reloading dashboard tab
            this._homeScreenService.reloadingAssignedTab$.subscribe((action) => {
                if (action.type === businessConstants.screenType.dashboard) {
                    this.reloadingDashboard = false;
                } else if (action.type === businessConstants.screenType.workflow) {
                    this.reloadingWorkflow = false;
                }
                this._changeDetectorRef.detectChanges();
            }),

            this._printService.actionSubject$.subscribe(action => {
                if (this.pages[this.selectedIndex] && this.pages[this.selectedIndex].screenType === businessConstants.screenType.research) {
                    this.pages[this.selectedIndex][businessConstants.homeScreen.singleConfigurationName].actionSubject$.next(action);
                }
            }),

            this._researchHelper.researchActionSubject$.subscribe(action => {
                if (this.pages[this.selectedIndex] && this.pages[this.selectedIndex].screenType === businessConstants.screenType.research) {
                    this.pages[this.selectedIndex][businessConstants.homeScreen.singleConfigurationName].actionSubject$.next(action);
                }
            }),
        );

        this.draggableTab = !this._deviceService.isMobile() && !this._deviceService.isTablet();
        this._changeDetectorRef.detectChanges();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        if (!this._changeDetectorRef['destroyed']) {
            // set the top of sub-chrome back to 119px
            this._layoutService.changeSubTitleTopOpen$.next('119px');
        }
        this.pages.forEach(element => {
            this._store.dispatch({
                type: this._screenHelperService.getDeleteScreenType(element.screenTpe),
                prefix: element.key
            });
        });
        this._unsubscribePrepareStore();
        this._store.dispatch({
            type: HomeScreenActionTypes.CLEAN_UP
        });
        this._store.dispatch({ type: ScreenActionTypes.SCREEN_STATUS_DICTIONARY_CLEAR });
        this._clearTimer();
    }

    onAddScreen(screen) {
        const pageId = this._createGUID();
        if (screen) {
            const page = {
                id: pageId,
                name: screen.caption,
                screenId: screen.id,
                screenType: screen.type,
                editable: false
            };

            // create related NgRx reducer
            this._createStoreForScreen(page).pipe(take(1)).subscribe(() => {
                if (screen.type === businessConstants.screenType.research) {
                    this._prepareStoreForResearchScreen(page).subscribe((state) => {
                        if (state.loading === 0) {
                            // add the page to container firstly
                            this._addNewTabToContainerAndSetPageIndex(page);
                            this._configNewResearchView(page);
                        } else if (state.loading < 0) {
                            this._deleteInvalidScreen(page);
                        }
                    });
                } else if (screen.type === businessConstants.screenType.dashboard) {
                    // add the page to container firstly
                    this._addNewTabToContainerAndSetPageIndex(page);
                    this._prepareStoreForDashboardScreen(page).subscribe(() => {
                        this._configNewTabView(page);
                    });
                } else if (screen.type === businessConstants.screenType.workflow) {
                    page['workflowId'] = this._homeScreenService.getWorkflowIdByScreen(screen);
                    this._addNewTabToContainerAndSetPageIndex(page);
                    this._configNewTabView(page);
                }

                if (page.screenId === businessConstants.allNotesScreenId || page.screenId === businessConstants.myNotesScreenId) {
                    page.editable = true;
                }
            });
        } else {
            const page = {
                id: pageId,
                name: businessConstants.homeScreen.newResearchTabName,
                screenId: businessConstants.homeScreen.prefixNewResearchViewID + businessConstants.homeScreen.screenIdConnectionIdentifier + this._createGUID(),
                screenType: businessConstants.screenType.research,
                configuration: new ResearchViewInfo(),
                editable: true
            };
            page.configuration.relationshipTreeInfo = {
                checkedNodeKeys: [],
                showAs: 'shortName',
            };
            // create related NgRx reducer
            this._store.dispatch({
                type: ResearchViewConstants.AddActionTypeName,
                prefix: page.id
            });
            // add new tab to home screen tabs
            this._addNewTabToContainerAndSetPageIndex(page);
            this._prepareStoreForResearchScreen(page).pipe(take(1)).subscribe(() => {
                this.pages[this.selectedIndex][businessConstants.homeScreen.singleConfigurationName] = new ResearchScreenConfigs(pageId, false);
                // prepare research view configurion
                page.configuration = this._homeScreenService.prepareResearchViewInfo(page.id);
                this._homeScreenService.updateIndividualScreen(this.homeScreen.id, this.pages, false).subscribe(() => {
                    this._changeDetectorRef.detectChanges();
                });
            });
        }
        this._changeDetectorRef.detectChanges();
    }

    onTabSelectChange(index: number) {
        if (this.selectedIndex !== index) {
            if (this.isScreenLoading[this.pages[index].id]) {
                this._delaySelectIndex = index;
                this.currentPageId = this.pages[index].id;
            } else {
                this._changeTab(index);
            }
            this._changeDetectorRef.detectChanges();
        }
        this._queue.prioritize(this.pages[index].id);
    }

    onTabSort(data) {
        this.pages.splice(data.upIndex, 0, this.pages.splice(data.downIndex, 1)[0]);
        this.selectedIndex = data.selectedIndex;
        if (this.pages) {
            this._homeScreenService.updateIndividualScreen(this.homeScreen.id, this.pages, false).subscribe(() => {
                this._changeDetectorRef.detectChanges();
            });
        }
    }

    onTabTitleBlur(data) {
        if (data && data.title) {
            this.pages[data.index].editing = false;
            if (data && data.newValue && data.newValue.trim().length === 0) {
                return;
            } else if (data.newValue && data.newValue.length > 32) {
                const subscription = this._alertWindow.error(businessConstants.tab.renamedFailed.alertTitle,
                    [businessConstants.tab.renamedFailed.alertContent]).subscribe(() => {
                        subscription.unsubscribe();
                    });
            } else {
                if (this.pages[data.index].name !== data.title) {
                    this.pages[data.index].name = data.title;
                    this.pages[data.index].editing = false;
                    this._homeScreenService.updateIndividualScreen(this.homeScreen.id, this.pages, false).subscribe(() => {
                    });
                }
            }
            this._changeDetectorRef.detectChanges();
        }
    }

    showPageContextMenu(event, item) {
        if (item.screenId && (item.screenId.indexOf(businessConstants.homeScreen.prefixNewResearchViewID) > -1
            || item.screenId === businessConstants.myNotesScreenId
            || item.screenId === businessConstants.allNotesScreenId)) {
            this.contextMenuConfig.items[0].disabled = false;
        } else {
            this.contextMenuConfig.items[0].disabled = true;
        }
        if (this.pages.length === 1) {
            this.contextMenuConfig.items[1].disabled = true;
        } else {
            this.contextMenuConfig.items[1].disabled = false;
        }
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, item));
    }

    showPagesContextMenu(event: MouseEvent, item: any) {
        this._contextMenuService.show(this.menuEle, { x: event.clientX, y: event.clientY });
        event.preventDefault();
        event.stopPropagation();
    }

    trackByFn(index, item) {
        return item.id;
    }
    //#endregion

    //#region Private properties
    private _actionMessageHandler(action) {
        if (action.type === HomeScreenActionTypes.DELETE) {
            this.isPrepareDataReady = false;
        } else if (action.type === HomeScreenActionTypes.DELETE_SUCCESS) {
            this.isPrepareDataReady = true;
            this._toast.notify({
                style: NotificationStyles.Success,
                message: businessConstants.common.layoutDeleteSuccessfully,
            });
            // if the layout is default layout, update store
            const userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
            if (userSetting && userSetting.defaultHomeLayout && this.homeScreen.id === userSetting.defaultHomeLayout.configuration.id) {
                delete userSetting.defaultHomeLayout;
                this._store.dispatch({
                    type: UsersActionTypes.UPDATE_USER_SETTING_SUCCESS,
                    payload: userSetting
                });
            }
            // reload the homescreen tabs before navigate to home
            this._store.dispatch({
                type: HomeScreenActionTypes.LOAD
            });
        } else if (action.type === HomeScreenActionTypes.DELETE_FAILED) {
            this.isPrepareDataReady = true;
            this._toast.notify({
                style: NotificationStyles.Success,
                message: businessConstants.common.layoutDeleteFailed,
            });
        }
    }

    private _addNewHomescreenTab(page) {
        this.loadingSpecificScreen = true;
        return this._homeScreenService.updateIndividualScreen(this.homeScreen.id, this.pages, false);
    }

    private _addNewTabToContainerAndSetPageIndex(page) {
        this.pages.push(page);
        this.currentPageId = page.id;
        this.selectedIndex = this.pages.length - 1;
        this._changeDetectorRef.detectChanges();
    }

    private _changeTab(index) {
        const page = this.pages[index];
        this.currentPageId = page.id;
        this.selectedIndex = index;
        this.currentPageName = page.name;
        // should load float menu when changing tab in homescreen, the menu will be hidden in the case which needn't to show it.
        this._floatMenuService.floatMenuBottomSubject$.next(true);
        if (page.screenType === businessConstants.screenType.research && page.config) {
            page.config.actionSubject$.next({ type: 'TAB_CLICK' });
        }
        if (page.screenType === businessConstants.screenType.dashboard) {
            this.reloadingDashboard = true;
            const screenInfo = {
                id: page.screenId,
                type: page.screenType,
                storeKey: page.id
            };
            this.actionSubject$.next({
                type: 'REFRESH_DASHBOARD',
                payload: screenInfo
            });
        } else if (page.screenType === businessConstants.screenType.workflow) {
            this.reloadingWorkflow = true;
            const screenInfo = {
                id: page.id,
                type: page.screenType,
                screenId: page.screenId,
                workflowId: this._homeScreenService.getWorkflowIdByScreen(page),
            };
            this.actionSubject$.next({
                type: 'REFRESH_WORKFLOW',
                payload: screenInfo
            });
        }
    }

    private _clearTimer(): void {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    private _configResearchView(page) {
        page.config = new ResearchScreenConfigs(page.id, false);
        this.isScreenLoading[page.id] = false;
        if (!this._changeDetectorRef['destroyed']) {
            this._changeDetectorRef.detectChanges();
        }
    }

    private _configNewTabView(page) {
        // add new tab to home screen tabs
        this.loadingSpecificScreen = false;
        this._addNewHomescreenTab(page).pipe(take(1)).subscribe(() => {
            this._changeTab(this.pages.length - 1);
            this._changeDetectorRef.detectChanges();
        });
    }

    private _configNewResearchView(page) {
        // add new tab to home screen tabs
        this._addNewHomescreenTab(page).pipe(
            take(1),
            catchError(err => {
                return of(null);
            }),
            switchMap(res => {
                if (res) {
                    if (this.pages[this.selectedIndex]) {
                        this.pages[this.selectedIndex][businessConstants.homeScreen.singleConfigurationName] = new ResearchScreenConfigs(page.id, false);
                    }
                } else {
                    this._deleteInvalidScreen(page);
                }
                return of(null);
            }),
        ).subscribe(() => {
            this._changeDetectorRef.detectChanges();
        });
    }

    private _createGUID() {
        return UUID.UUID().replace(/-/g, '');
    }

    // create research view info by id. (only for all notes / my notes)
    private _createResearchViewInfoById(screenId) {
        const researchViewInfo = new ResearchViewInfo();
        if (screenId === businessConstants.allNotesScreenId) {
            researchViewInfo.finderInfo = {
                entityId: this.currentUser.id,
                status: ResearchFocusStatus.ALL
            };
            researchViewInfo.relationshipTreeInfo = {
                checkedNodeKeys: [],
                showAs: 'shortName',
            };
            researchViewInfo.layoutInfo.isEntityFocusHidden = true;
            researchViewInfo.layoutInfo.isRelationshipTreeHidden = true;
        } else if (screenId === businessConstants.myNotesScreenId) {
            researchViewInfo.finderInfo = {
                entityId: this.currentUser.id,
                status: ResearchFocusStatus.NONE
            };
            researchViewInfo.relationshipTreeInfo = {
                checkedNodeKeys: [],
                showAs: 'shortName',
            };
            researchViewInfo.layoutInfo.isEntityFocusHidden = true;
            researchViewInfo.layoutInfo.isRelationshipTreeHidden = true;
        }
        return researchViewInfo;
    }

    private _createScreenMenu() {
        this.screenMenuContainer = [
            new ScreenMenu('01', businessConstants.research.screenMenuName, businessConstants.screenType.research, businessConstants.research.screenMenuIconName, null, businessConstants.research.screenMenuTid),
            new ScreenMenu('02', businessConstants.dashboard.screenMenuName, businessConstants.screenType.dashboard, businessConstants.dashboard.screenMenuIconName, null, businessConstants.dashboard.screenMenuTid),
            new ScreenMenu('03', businessConstants.workflow.screenMenuName, businessConstants.screenType.workflow, businessConstants.workflow.screenMenuIconName, null, businessConstants.workflow.screenMenuTid)
        ];
    }

    // create store for screen
    private _createStoreForScreen(tab): Observable<any> {
        if (tab.screenType === businessConstants.screenType.research) {
            const result = this._store.select(smartSelector(tab.id, ResearchViewConstants.ReducerName)).pipe(
                filter(state => state),
                take(1)
            );

            const action = {
                type: this._screenHelperService.getAddScreenType(tab.screenType),
                prefix: tab.id
            };
            this._store.dispatch(action);

            return result;
        } else if (tab.screenType === businessConstants.screenType.dashboard) {
            const result = this._store.select(smartSelector(tab.id, DashboardViewConstants.ReducerName)).pipe(
                filter(state => state),
                take(1)
            );

            const action = {
                type: this._screenHelperService.getAddScreenType(tab.screenType),
                prefix: tab.id
            };
            this._store.dispatch(action);

            return result;
        } else {
            return of(null);
        }
    }

    private _deleteInvalidScreen(page) {
        const tempIndex: number = this.pages.findIndex(item => item.id === page.id);
        if (tempIndex > -1) {
            this.pages.splice(tempIndex, 1);
        }
        this._store.dispatch({
            type: ScreenActionTypes.SCREEN_DICTIONARY_DELETE,
            payload: {
                id: page.screenId,
            },
        });
        this._store.dispatch({
            type: SystemActionTypes.DELETE_SCREEN,
            payload: {
                id: page.screenId,
            },
        });
        this._deleteStoreForScreen(page);
    }

    // delete store for screen
    private _deleteStoreForScreen(tab): void {
        const action = {
            type: this._screenHelperService.getDeleteScreenType(tab.screenType),
            prefix: tab.id
        };
        this._store.dispatch(action);
    }

    private _getTaskPrepareStoreForTab(page) {
        return () => {
            // create related NgRx reducer
            return this._createStoreForScreen(page).pipe(
                mergeMap(() => {
                    // set edit permission for tab
                    if (page.screenId.indexOf(businessConstants.homeScreen.prefixNewResearchViewID) > -1 ||
                        page.screenId === businessConstants.allNotesScreenId ||
                        page.screenId === businessConstants.myNotesScreenId) {
                        page.editable = true;
                    } else {
                        page.editable = false;
                    }

                    if (page.screenType === businessConstants.screenType.dashboard) {
                        // put the dashboard screen to observables
                        return this._prepareStoreForDashboardScreen(page).pipe(map(() => {
                            this.isScreenLoading[page.id] = false;
                            if (!this._changeDetectorRef['destroyed']) {
                                this._changeDetectorRef.detectChanges();
                            }
                            return page;
                        }));
                    } else if (page.screenType === businessConstants.screenType.workflow) {
                        this.isScreenLoading[page.id] = false;
                        if (!this._changeDetectorRef['destroyed']) {
                            this._changeDetectorRef.detectChanges();
                        }
                        page.workflowId = this._homeScreenService.getWorkflowIdByScreen(page);
                        return of(page);
                    } else {
                        return this._prepareStoreForResearchScreen(page).pipe(map((state) => {
                            if (state.loading < 0) {
                                this._errorPages.push(page);
                            }
                            this._configResearchView(page);
                            return page;
                        }));
                    }
                })
            );
        };
    }

    private _init() {
        this.loadingSpecificScreen = true;
        this.isPrepareDataReady = false;
        // init context menu
        this._initContextMenu();
        // set current user
        this.currentUser = this._storeQuerier.getCurrentUser();
        // set the screen menu
        this._setHomeScreenMenu();
        // load layout
        this._store.dispatch({
            type: HomeScreenActionTypes.LOAD
        });
        this._changeDetectorRef.detectChanges();
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.single = true;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.items = [
            { id: '0', text: businessConstants.contextMenu.renamedTab },
            { id: '1', text: businessConstants.contextMenu.closeTab }
        ];
        this._destroySubscriptions.push(
            this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event))
        );
        this._changeDetectorRef.detectChanges();
    }

    private _loadHomeScreenByLayoutId(homeScreens, layoutId) {
        if (homeScreens && homeScreens.length > 0) {
            if (layoutId === '') {
                this.homeScreen = homeScreens[0];
                // update store manually
                this._store.dispatch({
                    type: HomeScreenActionTypes.CHANGE_LAYOUT,
                    payload: this.homeScreen.id
                });
                this._onlyUpdateStore = true;
            } else {
                for (let i = 0; i < homeScreens.length; i++) {
                    if (homeScreens[i].id === layoutId) {
                        this.homeScreen = homeScreens[i];
                        break;
                    }
                }
                if (!this.homeScreen) {
                    this.homeScreen = homeScreens[0];
                }
            }

            const pages = this.homeScreen.tabs;
            // if the dashboard is the firest tab in homescreen, it will be loaded when loading home screen.
            if (pages && pages.length > 0) {
                if (pages[0].screenType === businessConstants.screenType.dashboard ||
                    pages[0].screenType === businessConstants.screenType.workflow) {
                    this.firstTabPageId = pages[0].id;
                }

                this.pages = pages;
                // set current page info
                this._setCurrentPageInfo(this.homeScreen);

                this._prepareStores(pages);
            } else {
                this.isPrepareDataReady = true;
                this.pages = [];
                // set current page info
                this._setCurrentPageInfo(this.homeScreen);
            }
        }
    }

    private _loadScreens(screenList) {
        this._homeScreenService.setScreenMenuItems(screenList, this.screenMenuContainer);
        this._changeDetectorRef.detectChanges();
    }

    private _onMenuSelect(event) {
        const actionId = event.payload.id;
        const data = event.payload.data;
        switch (actionId) {
            case '0':
                data.editing = true;
                break;
            case '1':
                if (this.pages.length > 1) {
                    const deleteIndex = this.pages.indexOf(data);
                    const isChangeTab = deleteIndex === this.selectedIndex;
                    if (this.selectedIndex >= deleteIndex && this.selectedIndex !== 0) {
                        this.selectedIndex--;
                    }
                    const deleteTitle = this.pages[deleteIndex].name;
                    this.pages.splice(deleteIndex, 1);
                    this._homeScreenService.updateIndividualScreen(this.homeScreen.id, this.pages, false).pipe(
                        take(1),
                        catchError(() => {
                            return of();
                        })
                    ).subscribe(() => {
                        const msg = deleteTitle + businessConstants.homeScreen.deleteTabMsg;
                        // popup a toast notification
                        this._toast.notify({
                            message: msg,
                            style: NotificationStyles.Success
                        });
                        const page = this.pages[this.selectedIndex];
                        this.currentPageId = this.pages[this.selectedIndex].id;
                        if (isChangeTab && page) {
                            if (page.screenType === businessConstants.screenType.dashboard) {
                                const screenInfo = {
                                    id: page.screenId,
                                    type: page.screenTypebusinessConstants,
                                    storeKey: page.id
                                };
                                this.loadingSpecificScreen = false;
                                this.actionSubject$.next({
                                    type: 'REFRESH_DASHBOARD',
                                    payload: screenInfo
                                });
                            } else if (page.screenType === businessConstants.screenType.workflow) {
                                const screenInfo = {
                                    id: page.id,
                                    type: page.screenType,
                                    screenId: page.screenId,
                                    workflowId: this._homeScreenService.getWorkflowIdByScreen(page),
                                };
                                this.loadingSpecificScreen = false;
                                this.actionSubject$.next({
                                    type: 'REFRESH_WORKFLOW',
                                    payload: screenInfo
                                });
                            }
                        }
                        this._changeDetectorRef.detectChanges();
                    });
                }
                break;
            default:
                break;
        }
    }

    private _onSubscribeChangeHomeScreenLayout(res) {
        if (res && res.length > 0) {
            if (!this._onlyUpdateStore) {
                this.isPrepareDataReady = false;
                const layoutId = res;
                const homeScreens = this._storeQuerier.queryBySelector(getHomeScreenSelector);
                this._loadHomeScreenByLayoutId(homeScreens.data, layoutId);
                this._changeDetectorRef.detectChanges();
            }
            this._onlyUpdateStore = false;
        }
    }

    private _onSubscribeHomeScreens(res) {
        if (res && res.data.length > 0 && res.type !== businessConstants.homeScreen.newLayoutName) {
            this.isPrepareDataReady = false;
            const homeScreens = res.data;
            let layoutId = '';
            const userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
            if (userSetting.defaultHomeLayout) {
                layoutId = userSetting.defaultHomeLayout.configuration.id;
            }
            this._loadHomeScreenByLayoutId(homeScreens, layoutId);
            this._changeDetectorRef.detectChanges();
        }
    }

    private _onSubscribeResearchChangeStatus(researchStatus) {
        if (researchStatus.type === ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS &&
            researchStatus.payload) {
            this._clearTimer();
            this._timer = setTimeout(() => {
                const screenKey = researchStatus.payload.screenKey;

                this._homeScreenService.updateIndividualScreen(this.homeScreen.id, this.pages, screenKey).pipe(take(1)).subscribe(() => {
                    this._store.dispatch({
                        type: ResearchScreenActionTypes.SET_HOME_SCREEN_CHANGE_STATUS,
                        payload: false
                    });
                    // save change research view to store
                    this._store.dispatch({ type: ScreenActionTypes.SCREEN_STATUS_DICTIONARY, payload: screenKey });
                    if (!this._changeDetectorRef[businessConstants.common.changeDetectorDestroyedIdentifier]) {
                        this._changeDetectorRef.detectChanges();
                    }
                });
            }, AUTO_SAVE_INTERVAL_5);
        }
    }

    private _onSubscribeShareHomeScreenLayout(res) {
        if (res) {
            this._homeScreenService.getHomeScreenLayoutById(res).pipe(take(1)).subscribe(layout => {
                this.homeScreen = layout;
                this.pages = layout.tabs;
                this.pages.forEach(element => {
                    if (element.id) {
                        // create store for the screen
                        this._destroySubscriptions.push(this._createStoreForScreen(element).subscribe(
                            () => {
                                // set edit permission for tab
                                if (element.screenId.indexOf(businessConstants.homeScreen.prefixNewResearchViewID) > -1 ||
                                    element.screenId === businessConstants.allNotesScreenId ||
                                    element.screenId === businessConstants.myNotesScreenId) {
                                    element.editable = true;
                                } else {
                                    element.editable = false;
                                }
                                // create research view info by all notes/ my notes id
                                const researchViewInfo = this._createResearchViewInfoById(element.screenId);
                                // set store for all notes/my notes
                                this._setStoreForAllMyNotes(element.id, researchViewInfo);
                                // set config for tab
                                element[businessConstants.homeScreen.singleConfigurationName] = new ResearchScreenConfigs(element.id, false);
                            }
                        ));
                    }
                });
                // set the first tab as default loading tab
                this.currentPageId = this.pages[0].id;
                this.currentPageName = this.pages[0].name;

                // update store manually
                this._store.dispatch({
                    type: HomeScreenActionTypes.CHANGE_LAYOUT,
                    payload: this.homeScreen.id
                });
            });
            this._changeDetectorRef.detectChanges();
        }
    }

    // prepare dashboard store
    private _prepareStoreForDashboardScreen(page) {
        let focusInfo = new FocusInfo();
        focusInfo = page.configuration;
        this._store.dispatch(new DashboardScreenInitializeFocus(page.id, focusInfo));
        return this._whenStoreDashboardStateReady(page);
    }

    private _prepareStoreForResearchScreen(page): Observable<any> {
        this._store.dispatch(
            new ResearchScreenInitialization(
                page.id,
                new ResearchScreenInitializationPayload(
                    page,
                    this.currentUser
                )
            )
        );
        return this._whenStoreResearchStateReady(page);
    }

    private _prepareStores(pages: any[]) {
        this._unsubscribePrepareStore();
        const tasks: PriorityTaskQueueTask[] = [];
        this._errorPages = [];
        pages.forEach((element) => {
            this.isScreenLoading[element.id] = true;
            if (element.id) {
                tasks.push(new PriorityTaskQueueTask(element.id, this._getTaskPrepareStoreForTab(element)));
            }
        });
        this._changeDetectorRef.detectChanges();
        this._queue = new PriorityTaskQueue(tasks);
        this._prepareStoreSubscription = this._queue.getChannel().subscribe((event) => {
            if (event instanceof AllExecuted) {
                if (this._delaySelectIndex !== -1) {
                    this._changeTab(this._delaySelectIndex);
                    this._delaySelectIndex = -1;
                } else {
                    this._changeTab(0);
                }
                // Delete invalid screens due to private entity and so on
                this._errorPages.forEach(item => this._deleteInvalidScreen(item));
                this._errorPages = [];
                this.loadingSpecificScreen = false;
                // do following
                this.isPrepareDataReady = true;
                this._changeDetectorRef.detectChanges();
                this._unsubscribePrepareStore();
            } else if ((event instanceof TaskExecuted)) {
                if (this.pages[this.selectedIndex] && event.taskId === this.pages[this.selectedIndex].id) {
                    // set the first tab as default loading tab
                    this._changeTab(this.selectedIndex);
                    // set current page info
                    this._setCurrentPageInfo(this.homeScreen);
                }
                // this._configResearchView(this.pages[this.selectedIndex]);
            }
        });
        this._queue.run();
    }

    private _setCurrentPageInfo(homeScreen) {
        const currentPageInfo: any = {
            id: homeScreen.id,
            name: homeScreen.name,
            type: businessConstants.homeScreen.pageTypeText
        };
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: currentPageInfo
        });
    }

    private _setHomeScreenMenu() {
        this._createScreenMenu();
        this._destroySubscriptions.push(
            this._store.pipe(
                select(visibleScreensSelector),
                filter(item => !!item),
            ).subscribe(items => this._loadScreens(items))
        );
    }

    private _setStoreForAllMyNotes(storeKey, researchViewInfo) {
        // layout
        this._store.dispatch(new ResearchScreenInitializeLayout(storeKey, researchViewInfo.layoutInfo));

        // entity focus
        const finderSetting: FinderSetting = {
            entity: EntityBrief.parse(this.currentUser),
            status: researchViewInfo.finderInfo.status
        };
        this._store.dispatch(new ResearchScreenInitializeFocus(storeKey, finderSetting));

        // relationship tree (depends on entity focus)
        const entity = Entity.parse(this.currentUser);
        const nodeData = RelationshipTreeService.parseToTreeViewModel(entity);
        const expandedKeys = nodeData.filter(node => !node.isLeaf).map(node => node.uuid);
        let checkedKeys = [];
        if (finderSetting.status === ResearchFocusStatus.ONE_DEGREE) {
            checkedKeys = nodeData.map(node => node.uuid);
        } else {
            checkedKeys = this._researchHelper.restoreCheckedKeys(researchViewInfo.relationshipTreeInfo.checkedNodeKeys, nodeData);
        }
        this._store.dispatch(new ResearchScreenSetRelationship(storeKey, {
            nodes: nodeData,
            expandedKeys: expandedKeys,
            checkedKeys: checkedKeys,
            showAs: researchViewInfo.relationshipTreeInfo.showAs
        }));
    }

    private _unsubscribePrepareStore() {
        if (this._prepareStoreSubscription && !this._prepareStoreSubscription.closed) {
            this._prepareStoreSubscription.unsubscribe();
            this._prepareStoreSubscription = null;
        }
    }

    private _whenStoreDashboardStateReady(page): Observable<any> {
        // dashboard only got {focus:xxx} stored in ngrx store
        return this._store.select(smartSelector(page.id, DashboardViewConstants.ReducerName)).pipe(
            filter((state) => state && state.focus),
            take(1));
    }

    private _whenStoreResearchStateReady(page): Observable<any> {
        return this._store.select(smartSelector(page.id, ResearchViewConstants.ReducerName)).pipe(
            filter((state) => state && state.loading < 1),
            take(1));
    }
    //#endregion
}
